/**
 * Iter 85.17 · Legacy brief-body → Markdown converter.
 *
 * Meeting briefs posted BEFORE Iter 85.17 have plain-text bodies stored
 * with an ALL-CAPS section header + "  • bullet" scaffold, e.g.:
 *
 *   SUMMARY
 *   text here
 *
 *   DECISIONS
 *     • Decision one
 *     • Decision two
 *
 *   ACTION ITEMS
 *     • Task one — Owner · due 2026-08-13
 *
 *   ↳ Source meeting: {uuid}
 *
 * Those bodies contain no Markdown syntax, so the MarkdownDocument
 * memo renderer treats the whole thing as one dense paragraph. This
 * shim detects the legacy shape and converts it into the exact
 * Markdown format the new `_render_brief_body` emits so both old and
 * new briefs render identically in the live UI without a data
 * migration blocking the roll-out.
 *
 * Detection rule: body contains ANY of the legacy uppercase section
 * headers on a line by themselves AND does NOT contain the new
 * `# Meeting Brief` H1 marker. Otherwise the body passes through
 * untouched.
 */

const LEGACY_SECTIONS = ["SUMMARY", "DECISIONS", "ACTION ITEMS", "KEY POINTS", "OPEN QUESTIONS", "FOLLOW-UPS"];

const SECTION_TO_HEADING = {
  SUMMARY: "## Summary",
  DECISIONS: "## Decisions",
  "ACTION ITEMS": "## Action Items",
  "KEY POINTS": "## Key Points",
  "OPEN QUESTIONS": "## Open Questions",
  "FOLLOW-UPS": "## Follow-Ups",
};

/** Returns true if the body looks like a legacy plain-text brief. */
export function isLegacyBriefBody(body) {
  if (!body || typeof body !== "string") return false;
  if (/^#\s+Meeting Brief\b/m.test(body)) return false; // already new-format
  const lines = body.split(/\r?\n/);
  return lines.some((l) => LEGACY_SECTIONS.includes(l.trim()));
}

/**
 * Convert a legacy plain-text brief body into the Iter 85.17 Markdown
 * shape. Idempotent — if the body is already new-format, returns it
 * unchanged.
 *
 * @param body   raw stored body (may be legacy or already Markdown)
 * @param note   the meeting-brief note itself (used for masthead)
 * @param client resolved client doc {name} (optional)
 * @param eng    resolved engagement doc {name} (optional)
 * @returns Markdown string ready for MarkdownDocument variant="memo"
 */
export function toMemoMarkdown(body, note, client, eng) {
  if (!body) return "";
  if (!isLegacyBriefBody(body)) return body;

  const lines = body.split(/\r?\n/);
  const out = [];

  // ── Masthead ──────────────────────────────────────────────────────
  const clientName = client?.name || "—";
  const engName = eng?.name || "—";
  const dateIso = note?.meeting_date || note?.created_at || "";
  let dateStr = "";
  if (dateIso && typeof dateIso === "string") {
    const iso = dateIso.slice(0, 10);
    const parts = iso.split("-");
    if (parts.length === 3) {
      const d = new Date(Date.UTC(+parts[0], +parts[1] - 1, +parts[2]));
      dateStr = d.toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      });
    }
  }
  const author = note?.author_name || "—";

  out.push("# Meeting Brief", "");
  out.push(`**Client:** ${clientName}  `);
  out.push(`**Engagement:** ${engName}  `);
  if (dateStr) out.push(`**Date:** ${dateStr}  `);
  out.push(`**Prepared by:** ${author}`, "");

  // ── Section walk ──────────────────────────────────────────────────
  let currentSection = null;
  let currentBuffer = [];
  const flush = () => {
    if (!currentSection) return;
    const heading = SECTION_TO_HEADING[currentSection];
    if (!heading) { currentBuffer = []; return; }
    out.push(heading, "");
    if (currentSection === "SUMMARY") {
      // Paragraph text — join lines, single blank between paragraphs.
      const joined = currentBuffer.join("\n").trim();
      if (joined) out.push(joined, "");
    } else if (currentSection === "ACTION ITEMS") {
      // Iter 85.17 · Each legacy row is "  • Task — Owner · due X".
      // Emit as its own bold-task + hard-break + Owner/Due paragraph
      // block so ReactMarkdown gives us distinct <p> per action.
      for (const raw of currentBuffer) {
        const line = raw.replace(/^\s*[•·]\s*/, "").trim();
        if (!line) continue;
        let task = line, owner = "", due = "";
        const dashIdx = line.search(/\s[—–-]\s/);
        if (dashIdx > 0) {
          task = line.slice(0, dashIdx).trim();
          const meta = line.slice(dashIdx + 3).trim();
          const parts = meta.split(/\s*·\s*/);
          if (parts.length >= 1) owner = parts[0].trim();
          if (parts.length >= 2) {
            due = parts[1].replace(/^due\s*/i, "").trim();
          }
        }
        out.push(`**${task}**  `); // two trailing spaces = hard break
        out.push(`Owner: ${owner || "Unassigned"} · Due: ${due || "Unspecified"}`);
        out.push("");
      }
    } else {
      // Plain bullet list — DECISIONS / KEY POINTS / OPEN QUESTIONS / FOLLOW-UPS.
      for (const raw of currentBuffer) {
        const line = raw.replace(/^\s*[•·]\s*/, "").trim();
        if (line) out.push(`- ${line}`);
      }
      out.push("");
    }
    currentBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    const trimmed = line.trim();
    if (LEGACY_SECTIONS.includes(trimmed)) {
      flush();
      currentSection = trimmed;
      continue;
    }
    // Provenance footer (legacy) — strip; we'll rebuild it below.
    if (/^↳\s*Source meeting:/i.test(trimmed)) continue;
    if (currentSection) currentBuffer.push(line);
  }
  flush();

  // ── Provenance footer ─────────────────────────────────────────────
  const sourceId = note?.source_meeting_note_id || "(unknown)";
  out.push("---", "", `*Source meeting: ${sourceId}*`);

  return out.join("\n").trim();
}
